import {Component, computed, inject} from "@angular/core";
import {PeerPracticeSession} from "@models";
import {DatePipe} from "@angular/common";
import {MatDivider} from "@angular/material/divider";
import {MatIcon} from "@angular/material/icon";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatOption, MatSelect} from "@angular/material/select";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {Translations} from "@translations";
import {CreatePeerPracticeSessionDialogStateService} from "../../service/create-peer-practice-session-dialog-state.service";
import {FormsModule} from "@angular/forms";
import {MatOptionSelectionChange} from "@angular/material/core";
import {SessionRunVectorComponent} from "@common/components/session/session-run-vector/session-run-vector.component";
import {SimTimeService} from "../../../../../../../core/services/sim-services/sim-time.service";

type ScheduleEntryHelper = {
	runId: string;
	start: Date;
	end: Date;
	intermissionId?: string | undefined;
	intermissionDuration?: number | undefined;
};

@Component({
	selector: "sim-peer-practice-session-step3",
	standalone: true,
	imports: [
		MatDivider,
		MatIcon,
		MatFormField,
		MatSelect,
		MatOption,
		MatLabel,
		TranslateModule,
		FormsModule,
		DatePipe,
		SessionRunVectorComponent,
	],
	templateUrl: "./peer-practice-session-step3.component.html",
	styleUrl: "./peer-practice-session-step3.component.scss",
})
export class PeerPracticeSessionStep3Component {
	readonly translate = inject(TranslateService);
	// Properties
	protected readonly T = Translations;

	// Injections
	readonly #stateService = inject(CreatePeerPracticeSessionDialogStateService);

	// Derived Properties
	protected readonly intermissionOptions = this.#stateService.intermissionOptions;
	protected readonly schedule = computed<ScheduleEntryHelper[]>(() => this.computeSchedule(this.#stateService.session()));

	protected async changeIntermission(changeEvent: MatOptionSelectionChange<number>, scheduleEntryId: string): Promise<void> {
		if (!changeEvent.source.value || !changeEvent.isUserInput) {
			return;
		}
		await this.#stateService.changeIntermission(changeEvent.source.value, scheduleEntryId);
	}

	private computeSchedule(session: PeerPracticeSession | null): ScheduleEntryHelper[] {
		if (!session) {
			return [];
		}

		let counter = new Date(session.startTime);
		const result: ScheduleEntryHelper[] = [];
		for (let i = 0; i < session.schedule.length; i += 2) {
			const run = session.schedule[i];
			const intermission = session.schedule[i + 1];

			if (!run) {
				throw new Error("Run is undefined");
			}

			const start = counter;
			const end = new Date(start.getTime() + run.duration);

			result.push({
				runId: run.id,
				start,
				end,
				intermissionId: intermission?.id,
				intermissionDuration: intermission?.duration ? SimTimeService.fromMillisecondsToMinutes(intermission.duration) : undefined,
			});

			counter = new Date(end.getTime() + intermission?.duration);
		}
		return result;
	}
}
